import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import DialogModal from 'tradera/components/dialog/dialog-modal';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';
import { useTranslator } from 'tradera/lang/translate';
import { Button } from 'tradera/components/atoms/button/button';
import { LocalLoader } from 'tradera/components/loader/loader';
import { saveFeedbackComment } from 'tradera/state/views/profile/actions';
import RatingNegativeIcon from '@tradera/blueprint/theme/icons2/rating-negative.svg?jsx';
import AlertIcon from '@tradera/blueprint/theme/icons2/alert.svg?jsx';

const validateText = (text, t) => {
    if (text?.trim().length === 0) {
        return t('profileRating_commentReview_noTextError');
    } else if (text?.length > 80) {
        return t('profileRating_commentReview_tooLongError');
    } else if (text?.match(/^[/\w\-–().:;,!?""”''´`åäöÅÄÖ€+\s$ ]+$/) === null) {
        return (
            t('profileRating_commentReview_charError') +
            ' ' +
            '" \' ´ ` ! ? , . ; : - ( ) / + - € $'
        );
    }
    return false;
};

const ReplyFeedbackModal = ({ feedbackItem, onClose }) => {
    const { t } = useTranslator();
    const dispatch = useDispatch();

    const [commentText, setCommentText] = useState('');
    const [commentInvalid, setCommentInvalid] = useState(true);
    const [isPosting, setIsPosting] = useState(false);
    const [hasPostError, setHasPostError] = useState(false);

    const {
        text,
        createdAgo,
        feedbackMemberAlias,
        feedBackMemberRoleDescription,
        gradeId,
        itemDescription,
        itemId
    } = feedbackItem;

    const handleTextChange = (text) => {
        setCommentText(text);
        setCommentInvalid(validateText(text, t));
    };

    const handleSendComment = async () => {
        try {
            setIsPosting(true);
            dispatch(
                saveFeedbackComment({
                    feedbackId: gradeId,
                    comment: commentText
                })
            );
        } catch {
            setIsPosting(false);
            setHasPostError(true);
        }
    };

    return (
        <DialogModal
            className="col-12 col-md-auto position-relative"
            onDismiss={() => onClose()}>
            {isPosting && <LocalLoader className="z-default" />}
            <div className="d-flex flex-column w-100 h-100">
                <div className="border-bottom py-2">
                    <h1 className="size-london mb-0 text-styled text-center">
                        {t('profileRating_commentReview')}
                    </h1>
                    <div
                        className="position-absolute"
                        style={{ top: '10px', right: '10px', zIndex: 1 }}>
                        <button
                            className="btn btn-round"
                            onClick={onClose}
                            title={'Close'}>
                            <CloseIcon className="icon" />
                        </button>
                    </div>
                </div>
                <div className="d-flex flex-column h-100 p-2">
                    <div className="d-flex alert alert-danger" role="alert">
                        <AlertIcon className="icon mr-1 my-auto" />
                        {t('profileRating_commentReview_alert')}
                    </div>
                    <div className="row mb-2">
                        <div className="col-auto">
                            <RatingNegativeIcon className="icon icon-md icon-danger mt-1" />
                        </div>
                        <div className="col d-flex flex-column">
                            <div className="d-flex mb-1">
                                <h3 className="mb-0">
                                    {feedbackMemberAlias}
                                    <span>
                                        ({feedBackMemberRoleDescription})
                                    </span>
                                </h3>
                                <span className="ml-auto">{createdAgo}</span>
                            </div>
                            <div>{text}</div>
                            <div>
                                {itemId} - {itemDescription}
                            </div>
                        </div>
                    </div>
                    <div className="mb-2">
                        <p
                            className="text-danger"
                            role="alert"
                            aria-label="error">
                            {commentInvalid}
                        </p>
                        <textarea
                            type="text"
                            id="commentText"
                            rows="8"
                            className="w-100"
                            placeholder={t(
                                'profileRating_commentReview_placeholder'
                            )}
                            onChange={(e) => {
                                handleTextChange(e.target.value);
                            }}
                        />
                    </div>
                    {hasPostError && (
                        <p className="text-center text-danger">
                            {t('profileRating_commentReview_postError')}
                        </p>
                    )}
                    <div className="row mt-auto">
                        <div className="col">
                            <Button
                                theme="danger"
                                fluid
                                onClick={() => onClose()}>
                                {t('profileRating_commentReview_cancel')}
                            </Button>
                        </div>
                        <div className="col">
                            <Button
                                theme="primary"
                                variant="outline"
                                fluid
                                disabled={!!commentInvalid}
                                onClick={() => {
                                    handleSendComment();
                                }}>
                                {t('profileRating_commentReview_send')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </DialogModal>
    );
};

export { ReplyFeedbackModal };
